export type FinancingProgram = {
  id: string;
  name: string;
  description: string;
  rateFactor?: number;
  termMonths?: number;
  offered?: boolean;
  amount?: number;
  financed?: boolean;
};

export type DownPaymentKind = 'nothing' | 'amount';

export type ContractDecisions = {
  financingProgramId: string | null;
  financingConfirmation: string | null;
  financingApplication?: {
    name?: string;
    email?: string;
    phone?: string;
    income?: string;
  } | null;
  downPaymentKind: DownPaymentKind | null;
  downPaymentAmount: number | null;
  downPaymentMethod: string | null;
  checkNumber: string | null;
  cardConfirmation: string | null;
};

export const DEFAULT_FINANCING_PROGRAMS: FinancingProgram[] = [
  {
    id: 'cash',
    name: 'Cash / pay-in-full',
    description: 'Pay the contract in full. No financing.',
    rateFactor: 1,
    termMonths: 0,
  },
  {
    id: '12mo',
    name: '12-month deferred',
    description: 'No down and no monthly payments for 12 months.',
    rateFactor: 1.05,
    termMonths: 12,
  },
  {
    id: '10yr',
    name: '10-year program',
    description: 'Long-term program with equal monthly payments.',
    rateFactor: 1.18,
    termMonths: 120,
  },
];

export function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

export function financedTotal(cashTotal: number, rateFactor = 1): number {
  return roundCents(Number(cashTotal || 0) * Number(rateFactor || 1));
}

export function isFinancedProgram(program?: FinancingProgram | null): boolean {
  if (!program) return false;
  if (typeof program.financed === 'boolean') return program.financed;
  return Number(program.rateFactor ?? 1) > 1;
}

export function readDecisions(raw: unknown): ContractDecisions {
  const record = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const kind =
    record.downPaymentKind === 'nothing' || record.downPaymentKind === 'amount'
      ? record.downPaymentKind
      : null;
  const amount = Number(record.downPaymentAmount);
  return {
    financingProgramId: record.financingProgramId ? String(record.financingProgramId) : null,
    financingConfirmation: record.financingConfirmation
      ? String(record.financingConfirmation).trim()
      : null,
    financingApplication:
      record.financingApplication && typeof record.financingApplication === 'object'
        ? (record.financingApplication as ContractDecisions['financingApplication'])
        : null,
    downPaymentKind: kind,
    downPaymentAmount: Number.isFinite(amount) ? roundCents(amount) : null,
    downPaymentMethod: record.downPaymentMethod ? String(record.downPaymentMethod) : null,
    checkNumber: record.checkNumber ? String(record.checkNumber).trim() : null,
    cardConfirmation: record.cardConfirmation ? String(record.cardConfirmation).trim() : null,
  };
}

export function findProgram(
  programs: FinancingProgram[] | undefined,
  id: string | null | undefined,
): FinancingProgram | null {
  if (!id) return null;
  const catalog = programs?.length ? programs : DEFAULT_FINANCING_PROGRAMS;
  return catalog.find((program) => program.id === id) ?? null;
}

export function isCheckMethod(value?: string | null): boolean {
  return /check/i.test(value ?? '');
}

export function isCardMethod(value?: string | null): boolean {
  return /card/i.test(value ?? '');
}

export function remainingDecisionKeys(
  decisions: ContractDecisions,
  program: FinancingProgram | null,
): string[] {
  const remaining: string[] = [];
  if (!decisions.financingProgramId || !program) remaining.push('finance');
  if (program && isFinancedProgram(program) && !decisions.financingConfirmation) {
    remaining.push('apply');
  }
  if (!decisions.downPaymentKind) {
    remaining.push('downpay');
    return remaining;
  }
  if (decisions.downPaymentKind === 'amount') {
    const amount = Number(decisions.downPaymentAmount || 0);
    if (!(amount > 0) || !decisions.downPaymentMethod) remaining.push('downpay');
    else if (isCheckMethod(decisions.downPaymentMethod) && !decisions.checkNumber) {
      remaining.push('downpay');
    } else if (isCardMethod(decisions.downPaymentMethod) && !decisions.cardConfirmation) {
      remaining.push('downpay');
    }
  }
  return remaining;
}

export function decisionsComplete(
  decisions: ContractDecisions,
  program: FinancingProgram | null,
): boolean {
  return remainingDecisionKeys(decisions, program).length === 0;
}

export function decisionBlockMessage(remaining: string[]): string {
  if (remaining.includes('finance')) return 'Pick a financing program before signing.';
  if (remaining.includes('apply')) {
    return 'Apply for financing and store the confirmation before signing.';
  }
  if (remaining.includes('downpay')) return 'Finish the down payment decision before signing.';
  return 'Finish remaining contract decisions before signing.';
}

export function cashTotalFromSummary(
  summary: { cashTotal?: number | string | null; finalTotal?: number | string | null; total?: number | string | null } | null | undefined,
  fallback = 0,
): number {
  const cash = Number(summary?.cashTotal);
  if (Number.isFinite(cash) && cash > 0) return cash;
  const displayed = Number(summary?.finalTotal ?? summary?.total ?? fallback);
  return Number.isFinite(displayed) ? displayed : fallback;
}

export function confirmationRows(decisions: ContractDecisions): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  if (decisions.financingConfirmation) {
    rows.push(['Financing approval', decisions.financingConfirmation]);
  }
  if (decisions.cardConfirmation) {
    rows.push(['Pay by card', decisions.cardConfirmation]);
  }
  if (decisions.checkNumber) {
    rows.push(['Check number', decisions.checkNumber]);
  }
  return rows;
}

export function decisionsFromContract(contract: {
  contractData?: { decisions?: unknown; display?: Record<string, unknown> };
  decisions?: unknown;
}): ContractDecisions {
  const fromRoot = readDecisions(contract.decisions);
  const fromData = readDecisions(contract.contractData?.decisions);
  const display = contract.contractData?.display ?? {};
  const merged = {
    ...fromData,
    ...Object.fromEntries(
      Object.entries(fromRoot).filter(([, value]) => value != null && value !== ''),
    ),
  } as ContractDecisions;
  if (!merged.downPaymentMethod && display.downPaymentMethod) {
    merged.downPaymentMethod = String(display.downPaymentMethod);
  }
  if (!merged.checkNumber && display.checkNumber) {
    merged.checkNumber = String(display.checkNumber);
  }
  if (!merged.cardConfirmation && display.cardConfirmation) {
    merged.cardConfirmation = String(display.cardConfirmation);
  }
  if (!merged.financingConfirmation && display.financingConfirmation) {
    merged.financingConfirmation = String(display.financingConfirmation);
  }
  return merged;
}
